import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { applyCourseBadges } from '@/lib/courseAppointmentBadge';
import {
  hideSeriesCancelFromRangeCaches,
  rememberSeriesCancelRule,
  seriesCancelRuleFromMembers,
  seriesCancelRules,
  withoutSeriesCancelHidden,
  type SeriesCancelMember,
} from '@/lib/seriesCancelCalendar';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { isAppointmentEditable as isAppointmentEditableStatus } from '@/utils/appointmentEditability';

export type AppointmentRow = Database['public']['Tables']['appointments']['Row'];
export type AppointmentStatus = Database['public']['Enums']['appointment_status'];
export type VisitStatus = Database['public']['Enums']['visit_status'];

export interface AppointmentListItem {
  id: string;
  clinic_id: string;
  patient_id: string | null; // Nullable for blocker appointments
  patient_name: string;
  clinician_id: string | null;
  clinician_name: string | null;
  service_id: string | null;
  service_name: string | null;
  service_color: string | null;
  /** True when the appointment type is a calendar block (Frokost and the like). */
  is_blocker?: boolean;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  visit_status: VisitStatus | null;
  notes: string | null;
  recurrence_pattern?: string;
  recurrence_parent_id?: string | null;
  is_recurrence_parent?: boolean;
  payment_method?: string | null;
  payment_status?: string | null;
  payment_forfeited_at?: string | null;
  source?: string | null;
  pre_visit_assignment_id?: string | null;
  treatment_course_step_id?: string | null;
  treatment_course_id?: string | null;
  course_badge?: { index: number; total: number } | null;
  video_meeting_url?: string | null;
  whereby_meeting_id?: string | null;
}

export interface ListFilters {
  start: string; // ISO
  end: string; // ISO
  clinicianId?: string | null;
}

export const appointmentsRangeQueryKey = (clinicId: string | null, filters: ListFilters) =>
  ['appointments', 'range', clinicId, filters] as const;

export const appointmentDetailQueryKey = (id: string | null | undefined) =>
  ['appointments', 'detail', id] as const;

/** Staff may edit/reschedule upcoming or completed (paid) appointments. Cancelled/no-show stay locked. */
export function isAppointmentEditable(status: AppointmentStatus, startsAt: string | Date): boolean {
  return isAppointmentEditableStatus(status, startsAt);
}

const APPOINTMENT_LIST_SELECT =
  'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, visit_status, notes, ' +
  'recurrence_pattern, recurrence_parent_id, is_recurrence_parent, payment_method, payment_status, payment_forfeited_at, source, pre_visit_assignment_id, ' +
  'treatment_course_step_id, video_meeting_url, whereby_meeting_id, ' +
  'patient:patients!appointments_patient_id_fkey(first_name,last_name), ' +
  'clinician:users!appointments_clinician_id_fkey(name,email), ' +
  'service:services!appointments_service_id_fkey(color, is_blocker)';

type AppointmentJoined = {
  id: string;
  clinic_id: string;
  patient_id: string | null;
  clinician_id: string | null;
  service_id: string | null;
  service_name: string | null;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  visit_status: VisitStatus | null;
  notes: string | null;
  recurrence_pattern?: string;
  recurrence_parent_id?: string | null;
  is_recurrence_parent?: boolean;
  payment_method: string | null;
  payment_status: string | null;
  payment_forfeited_at: string | null;
  source: string | null;
  pre_visit_assignment_id: string | null;
  treatment_course_step_id: string | null;
  video_meeting_url: string | null;
  whereby_meeting_id: string | null;
  patient: { first_name: string; last_name: string } | null;
  clinician: { name: string | null; email: string } | null;
  service: { color: string | null; is_blocker?: boolean | null } | null;
};

function mapAppointmentRow(r: AppointmentJoined): AppointmentListItem {
  return {
    id: r.id,
    clinic_id: r.clinic_id,
    patient_id: r.patient_id,
    patient_name: r.patient ? `${r.patient.first_name} ${r.patient.last_name}`.trim() : '—',
    clinician_id: r.clinician_id,
    clinician_name: r.clinician?.name ?? r.clinician?.email ?? null,
    service_id: r.service_id,
    service_name: r.service_name,
    service_color: r.service?.color ?? null,
    is_blocker: Boolean(r.service?.is_blocker),
    starts_at: r.starts_at,
    ends_at: r.ends_at,
    status: r.status,
    visit_status: r.visit_status,
    notes: r.notes,
    recurrence_pattern: r.recurrence_pattern,
    recurrence_parent_id: r.recurrence_parent_id,
    is_recurrence_parent: r.is_recurrence_parent,
    payment_method: r.payment_method,
    payment_status: r.payment_status,
    payment_forfeited_at: r.payment_forfeited_at,
    source: r.source,
    pre_visit_assignment_id: r.pre_visit_assignment_id,
    treatment_course_step_id: r.treatment_course_step_id,
    treatment_course_id: null,
    course_badge: null,
    video_meeting_url: r.video_meeting_url,
    whereby_meeting_id: r.whereby_meeting_id,
  };
}

async function attachCourseBadges(rows: AppointmentListItem[]): Promise<AppointmentListItem[]> {
  const stepIds = [
    ...new Set(
      rows.map((row) => row.treatment_course_step_id).filter((id): id is string => Boolean(id)),
    ),
  ];
  if (stepIds.length === 0) return rows;

  const { data: stepRows, error: stepError } = await supabase
    .from('treatment_course_steps')
    .select('id, sort_order, course_id')
    .in('id', stepIds);
  if (stepError) throw stepError;

  const courseIds = [...new Set((stepRows ?? []).map((step) => step.course_id).filter(Boolean))];
  const totals = new Map<string, number>();
  if (courseIds.length > 0) {
    const { data: allSteps, error: countError } = await supabase
      .from('treatment_course_steps')
      .select('id, course_id')
      .in('course_id', courseIds);
    if (countError) throw countError;
    for (const step of allSteps ?? []) {
      totals.set(step.course_id, (totals.get(step.course_id) ?? 0) + 1);
    }
  }
  return applyCourseBadges(rows, stepRows ?? [], totals);
}

export function useAppointmentsInRange(clinicId: string | null | undefined, filters: ListFilters) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: appointmentsRangeQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId && filters.start && filters.end),
    queryFn: async (): Promise<AppointmentListItem[]> => {
      if (!clinicId) return [];
      let query = supabase
        .from('appointments')
        .select(APPOINTMENT_LIST_SELECT)
        .eq('clinic_id', clinicId)
        .gte('starts_at', filters.start)
        .lte('starts_at', filters.end)
        .neq('status', 'cancelled')
        .order('starts_at', { ascending: true });

      if (filters.clinicianId) {
        query = query.eq('clinician_id', filters.clinicianId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = withoutSeriesCancelHidden(
        ((data ?? []) as unknown as AppointmentJoined[]).map(mapAppointmentRow),
        seriesCancelRules(qc),
      );
      return attachCourseBadges(rows);
    },
    staleTime: 15_000,
  });
}

export function useAppointment(id: string | null | undefined) {
  return useQuery({
    queryKey: appointmentDetailQueryKey(id ?? null),
    enabled: Boolean(id),
    queryFn: async (): Promise<AppointmentListItem> => {
      if (!id) throw new Error('No id');
      const { data, error } = await supabase
        .from('appointments')
        .select(APPOINTMENT_LIST_SELECT)
        .eq('id', id)
        .single();
      if (error) throw error;

      const [row] = await attachCourseBadges([
        mapAppointmentRow(data as unknown as AppointmentJoined),
      ]);
      return row;
    },
  });
}

export interface CreateAppointmentInput {
  clinic_id: string;
  patient_id: string | null; // Nullable for blocker appointments
  starts_at: string;
  ends_at: string;
  service_id?: string | null;
  clinician_id?: string | null;
  notes?: string | null;
  resource_ids?: string[] | null;
}

export interface CreateRecurringAppointmentInput {
  clinic_id: string;
  patient_id: string | null; // Nullable for blocker appointments
  starts_at: string;
  ends_at: string;
  service_id?: string | null;
  clinician_id?: string | null;
  notes?: string | null;
  recurrence_pattern: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  recurrence_end_date?: string | null;
  recurrence_count?: number | null;
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAppointmentInput): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('create_appointment', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id ?? undefined,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_service_id: input.service_id ?? undefined,
        p_clinician_id: input.clinician_id ?? undefined,
        p_notes: input.notes ?? undefined,
        p_resource_ids: input.resource_ids ?? undefined,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useCreateRecurringAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRecurringAppointmentInput): Promise<AppointmentRow[]> => {
      const { data, error } = await supabase.rpc('create_recurring_appointment_series', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id ?? undefined,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_service_id: input.service_id ?? undefined,
        p_clinician_id: input.clinician_id ?? undefined,
        p_notes: input.notes ?? undefined,
        p_recurrence_pattern: input.recurrence_pattern,
        p_recurrence_end_date: input.recurrence_end_date ?? undefined,
        p_recurrence_count: input.recurrence_count ?? undefined,
      } as never);
      if (error) throw error;
      return data as AppointmentRow[];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export interface RescheduleAppointmentInput {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  clinician_id?: string | null;
  reason?: string | null;
  resource_ids?: string[] | null;
}

export function useRescheduleAppointment() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: RescheduleAppointmentInput): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('reschedule_appointment', {
        p_appointment_id: input.appointment_id,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_clinician_id: input.clinician_id ?? undefined,
        p_reason: input.reason ?? undefined,
        p_resource_ids: input.resource_ids ?? undefined,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useCancelAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      reason?: string | null;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('cancel_appointment_with_policy', {
        p_appointment_id: input.appointment_id,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      return data as unknown as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient', row.patient_id] });
      qc.invalidateQueries({ queryKey: ['treatment-courses'] });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export interface FinishAppointmentResult {
  appointment_id: string;
  invoice_id: string | null;
  outcome: 'issued' | 'already_covered' | 'difference' | 'punch_card_pending';
  price_delta_cents: number;
}

export function useFinishAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<FinishAppointmentResult> => {
      const { data, error } = await supabase.rpc('finish_appointment_and_invoice', {
        p_appointment_id: id,
      });
      if (error) throw error;
      return data as unknown as FinishAppointmentResult;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.appointment_id) });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['appointments', 'punch-usage'] });
      qc.invalidateQueries({ queryKey: ['treatment-courses'] });
    },
  });
}

export function useCompleteAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('complete_appointment', {
        p_appointment_id: id,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['treatment-courses'] });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useMarkNoShowAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('mark_no_show_appointment', {
        p_appointment_id: id,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['treatment-courses'] });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useUpdateAppointmentNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      notes: string;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('update_appointment_notes', {
        p_appointment_id: input.appointment_id,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useUpdateAppointmentDuration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      new_ends_at: string;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('update_appointment_duration', {
        p_appointment_id: input.appointment_id,
        p_new_ends_at: input.new_ends_at,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

function cachedSeriesMembers(qc: QueryClient, anchorId: string): SeriesCancelMember[] {
  const members: SeriesCancelMember[] = [];
  const detail = qc.getQueryData<AppointmentListItem>(appointmentDetailQueryKey(anchorId));
  if (detail) members.push(detail);
  for (const [, rows] of qc.getQueriesData<AppointmentListItem[]>({
    queryKey: ['appointments', 'range'],
  })) {
    if (!rows) continue;
    members.push(...rows);
  }
  return members;
}

export function useDeleteAppointmentSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      delete_future_only: boolean;
      reason?: string | null;
    }): Promise<number> => {
      const rule = seriesCancelRuleFromMembers(
        cachedSeriesMembers(qc, input.appointment_id),
        input.appointment_id,
        input.delete_future_only,
      );
      const { data, error } = await supabase.rpc('delete_appointment_series', {
        p_appointment_id: input.appointment_id,
        p_delete_future_only: input.delete_future_only,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      const count = data as number;
      if (typeof count === 'number' && count > 0) {
        rememberSeriesCancelRule(qc, rule);
        hideSeriesCancelFromRangeCaches(qc);
      }
      return count;
    },
    onSuccess: (_count, variables) => {
      hideSeriesCancelFromRangeCaches(qc);
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(variables.appointment_id) });
    },
  });
}

export async function fetchUpcomingSeriesMembers(anchor: {
  id: string;
  recurrence_parent_id?: string | null;
}): Promise<{ id: string; starts_at: string; ends_at: string }[]> {
  const parentId = anchor.recurrence_parent_id || anchor.id;
  if (!/^[0-9a-f-]{36}$/i.test(parentId)) return [];
  const { data, error } = await supabase
    .from('appointments')
    .select('id, starts_at, ends_at')
    .or(`id.eq.${parentId},recurrence_parent_id.eq.${parentId}`)
    .eq('status', 'upcoming');
  if (error) throw error;
  return (data ?? []) as { id: string; starts_at: string; ends_at: string }[];
}

export function useUpdateAppointmentSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      update_future_only: boolean;
      notes?: string | null;
      clinician_id?: string | null;
    }): Promise<number> => {
      const { data, error } = await supabase.rpc('update_appointment_series', {
        p_appointment_id: input.appointment_id,
        p_update_future_only: input.update_future_only,
        p_notes: input.notes ?? undefined,
        p_clinician_id: input.clinician_id ?? undefined,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

export function useSetVisitStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      visit_status: VisitStatus;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('set_appointment_visit_status', {
        p_appointment_id: input.appointment_id,
        p_visit_status: input.visit_status,
      });
      if (error) throw error;
      return data as unknown as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['occupying-resource-allocations'] });
      qc.invalidateQueries({ queryKey: ['appointment-resource-allocations'] });
    },
  });
}

/**
 * Subscribes to Realtime INSERT/UPDATE/DELETE on the appointments table for
 * the active clinic and invalidates the visible range query on any change.
 * Call once per calendar mount; cleans up on unmount.
 */
export function useAppointmentsRealtime(clinicId: string | null | undefined, filters: ListFilters) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!clinicId) return;
    const channel = supabase
      .channel(`appointments:${clinicId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          qc.invalidateQueries({
            queryKey: appointmentsRangeQueryKey(clinicId, filters),
          });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, filters.start, filters.end, filters.clinicianId, qc]);
}
